import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  PopupRequest,
  PublicClientApplication,
} from '@azure/msal-browser';

export interface TokenExchangeResponse {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- OAuth response format
  access_token: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- OAuth response format
  expires_in: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- OAuth response format
  refresh_token?: string;
}

type TokenType =
  | 'urn:ietf:params:oauth:token-type:id_token'
  | 'urn:ietf:params:oauth:token-type:access_token';

const isNoTokenRequestCacheError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'errorCode' in error &&
  error.errorCode === 'no_token_request_cache_error';

const ensureNonEmptyToken = (token: string | undefined, errorMessage: string): string => {
  if (typeof token !== 'string' || token.length === 0) {
    throw new Error(errorMessage);
  }

  return token;
};

const getSubjectToken = async (
  msalInstance: PublicClientApplication,
  microsoftLoginRequest: PopupRequest,
  account: AccountInfo | null,
  loginResponse: AuthenticationResult,
): Promise<{ token: string; tokenType: TokenType }> => {
  if (loginResponse.idToken) {
    return {
      token: loginResponse.idToken,
      tokenType: 'urn:ietf:params:oauth:token-type:id_token',
    };
  }

  if (loginResponse.accessToken) {
    return {
      token: loginResponse.accessToken,
      tokenType: 'urn:ietf:params:oauth:token-type:access_token',
    };
  }

  if (!account) {
    throw new Error('Microsoft sign-in did not return an account to acquire an access token for.');
  }

  try {
    const silentResponse = await msalInstance.acquireTokenSilent({
      ...microsoftLoginRequest,
      account,
    });
    return {
      token: silentResponse.accessToken,
      tokenType: 'urn:ietf:params:oauth:token-type:access_token',
    };
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const popupResponse = await msalInstance.acquireTokenPopup({
        ...microsoftLoginRequest,
        prompt: 'select_account',
        account,
      });
      return {
        token: popupResponse.accessToken,
        tokenType: 'urn:ietf:params:oauth:token-type:access_token',
      };
    }
    throw error;
  }
};

export const resolveMsalAccount = (
  msalInstance: PublicClientApplication,
  storedHomeAccountId: string | undefined,
): AccountInfo | null => {
  const accounts = msalInstance.getAllAccounts();
  return (
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- account resolution can be empty at runtime
    msalInstance.getActiveAccount() ??
    accounts.find(cachedAccount => cachedAccount.homeAccountId === storedHomeAccountId) ??
    accounts[0] ??
    null
  );
};

export const signinWithMicrosoftTokenExchange = async ({
  msalReady,
  msalInstance,
  microsoftLoginRequest,
  setMsalAccountContext,
  exchangeMicrosoftToken,
  setSession,
}: {
  msalReady: Promise<void>;
  msalInstance: PublicClientApplication;
  microsoftLoginRequest: PopupRequest;
  setMsalAccountContext: (account: AccountInfo) => void;
  exchangeMicrosoftToken: (
    microsoftToken: string,
    subjectTokenType: TokenType,
  ) => Promise<TokenExchangeResponse>;
  setSession: (authResult: {
    expiresIn: number;
    accessToken: string;
    refreshToken?: string;
  }) => void;
}): Promise<void> => {
  await msalReady;
  try {
    await msalInstance.handleRedirectPromise();
  } catch (error) {
    if (!isNoTokenRequestCacheError(error)) {
      throw error;
    }
  }

  const loginResponse = await msalInstance.loginPopup({
    ...microsoftLoginRequest,
    prompt: 'select_account',
  });

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- account can be absent at runtime
  const account = loginResponse.account ?? msalInstance.getActiveAccount() ?? null;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- account can be null in edge popup flows
  if (account) {
    setMsalAccountContext(account);
  }

  const microsoftSubjectToken = await getSubjectToken(
    msalInstance,
    microsoftLoginRequest,
    account,
    loginResponse,
  );
  const tokenResponse = await exchangeMicrosoftToken(
    microsoftSubjectToken.token,
    microsoftSubjectToken.tokenType,
  );

  setSession({
    expiresIn: tokenResponse.expires_in,
    accessToken: tokenResponse.access_token,
    refreshToken: tokenResponse.refresh_token,
  });
};

export const bootstrapSessionFromMicrosoftAccountTokenExchange = async ({
  msalReady,
  msalInstance,
  microsoftLoginRequest,
  storedHomeAccountId,
  existingToken,
  isStoredTokenValid,
  onStoredTokenReuse,
  setMsalAccountContext,
  setAuthenticationHeaders,
  exchangeMicrosoftToken,
  setSession,
}: {
  msalReady: Promise<void>;
  msalInstance: PublicClientApplication;
  microsoftLoginRequest: PopupRequest;
  storedHomeAccountId: string | undefined;
  existingToken: string | undefined;
  isStoredTokenValid: boolean;
  onStoredTokenReuse?: (token: string) => void;
  setMsalAccountContext: (account: AccountInfo) => void;
  setAuthenticationHeaders: (token: string) => void;
  exchangeMicrosoftToken: (
    microsoftToken: string,
    subjectTokenType: TokenType,
  ) => Promise<TokenExchangeResponse>;
  setSession: (authResult: {
    expiresIn: number;
    accessToken: string;
    refreshToken?: string;
  }) => void;
}): Promise<boolean> => {
  await msalReady;

  const account = resolveMsalAccount(msalInstance, storedHomeAccountId);
  if (existingToken && isStoredTokenValid) {
    onStoredTokenReuse?.(existingToken);
    if (account) {
      setMsalAccountContext(account);
    }
    setAuthenticationHeaders(existingToken);
    return true;
  }

  if (!account) {
    return false;
  }

  setMsalAccountContext(account);

  try {
    const silentResponse = await msalInstance.acquireTokenSilent({
      ...microsoftLoginRequest,
      account,
    });

    const subjectToken = ensureNonEmptyToken(
      silentResponse.idToken || silentResponse.accessToken,
      'Microsoft silent token acquisition returned an empty token for token exchange.',
    );
    const subjectTokenType: TokenType = silentResponse.idToken
      ? 'urn:ietf:params:oauth:token-type:id_token'
      : 'urn:ietf:params:oauth:token-type:access_token';

    const tokenResponse = await exchangeMicrosoftToken(subjectToken, subjectTokenType);
    setSession({
      expiresIn: tokenResponse.expires_in,
      accessToken: tokenResponse.access_token,
      refreshToken: tokenResponse.refresh_token,
    });
    return true;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      return false;
    }
    throw error;
  }
};
